import { BanIcon, HourglassIcon, LoaderCircleIcon, LogOutIcon, RefreshCwIcon } from "lucide-react";
import { Link, Navigate } from "react-router";
import { toast } from "sonner";
import { Logo } from "@/components/brand/logo.tsx";
import { LanguageSwitch } from "@/components/common/language-switch.tsx";
import { useSignOut } from "@/components/shell/user-menu.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { useDocumentTitle, useMe } from "@/lib/queries.ts";
import { cn } from "@/lib/utils.ts";
import { FullPageLoader } from "./guards.tsx";

/** Pending approval or blocked. */
export function WaitlistPage() {
  const { t } = useI18n();
  const me = useMe();
  const signOut = useSignOut();
  const blocked = me.data?.tenant.status === "blocked";
  useDocumentTitle(blocked ? t.waitlist.blockedTitle : t.waitlist.pendingTitle, t.brand.name);

  if (me.isPending) return <FullPageLoader />;
  if (!me.data) return <Navigate to="/login" replace />;
  if (me.data.tenant.status === "active") return <Navigate to="/app" replace />;

  const refresh = async () => {
    const result = await me.refetch();
    if (result.data?.tenant.status === "pending") toast.message(t.waitlist.stillPending);
  };

  return (
    <div className="flex min-h-dvh flex-col bg-sidebar px-4 py-6">
      <header className="mx-auto flex w-full max-w-lg items-center justify-between">
        <span className="inline-flex items-center gap-2.5">
          <Logo size={28} className="text-primary dark:text-foreground" />
          <span className="text-[15px] font-bold">{t.brand.name}</span>
        </span>
        <LanguageSwitch />
      </header>
      <main className="flex flex-1 items-center justify-center py-10">
        <div className="w-full max-w-lg rounded-2xl border bg-card p-6 text-center shadow-[0_10px_40px_-12px_rgba(15,27,45,0.15)] sm:p-9">
          <span
            className={cn(
              "mx-auto flex size-14 items-center justify-center rounded-2xl",
              blocked ? "bg-danger-soft text-danger" : "bg-primary-soft text-primary-soft-foreground",
            )}
          >
            {blocked ? <BanIcon className="size-7" aria-hidden /> : <HourglassIcon className="size-7" aria-hidden />}
          </span>
          <h1 className="mt-5 text-[21px] leading-snug font-bold tracking-[-0.02em] text-balance">
            {blocked ? t.waitlist.blockedTitle : t.waitlist.pendingTitle}
          </h1>
          <p className="mt-3 text-[13.5px] text-muted-foreground">
            {t.waitlist.signedInAs} <span className="font-semibold text-foreground">{me.data.user.email}</span>
          </p>
          <p className="mx-auto mt-3 max-w-sm text-[13.5px] leading-relaxed text-muted-foreground">
            {blocked ? t.waitlist.blockedText : t.waitlist.pendingText}
          </p>
          <div className="mt-7 flex flex-col-reverse justify-center gap-2 sm:flex-row">
            <Button variant="ghost-muted" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
              <LogOutIcon aria-hidden />
              {t.common.signOut}
            </Button>
            {!blocked ? (
              <Button onClick={() => void refresh()} disabled={me.isFetching}>
                {me.isFetching ? <LoaderCircleIcon className="animate-spin" aria-hidden /> : <RefreshCwIcon aria-hidden />}
                {t.waitlist.refresh}
              </Button>
            ) : null}
          </div>
        </div>
      </main>
      <footer className="flex justify-center gap-4 text-[12px] text-muted-foreground">
        <Link to="/privacy" className="hover:text-foreground">
          {t.common.privacy}
        </Link>
        <Link to="/terms" className="hover:text-foreground">
          {t.common.terms}
        </Link>
      </footer>
    </div>
  );
}
